const { CarouselElement } = window.BAO.Carousel
const { setInitialised, useArrows } = window.BAO.Carousel.plugins

export class ProductCompareMedia extends CarouselElement {
  static get requiredElements () {
    return ['slider', 'slide']
  }

  /**
   * A single slide (or none) never needs to loop or be dragged, and
   * keen-slider's loop math isn't reliable with only one real slide -
   * so those cases skip keen-slider entirely. CSS (:has(:only-child) in
   * section-product-comparison.css) handles the resulting centred
   * layout and hides the arrows without any help from this component.
   *
   * @returns {boolean}
   */
  get hasMultipleSlides () {
    return this.els.slide.elements.length > 1
  }

  /** @returns {Promise<KeenSliderInstance|null>} */
  async initialiseCarousel () {
    if (!this.hasMultipleSlides) return null

    return super.initialiseCarousel()
  }

  beforeInitialiseCarousel () {
    this.attachCarouselClasses()
  }

  attachCarouselClasses () {
    this.els.slider.element.classList.add('keen-slider')
    this.els.slide.elements.forEach(slide => {
      slide.classList.add('keen-slider__slide')
    })
  }

  /** @returns {KeenSliderOptions} */
  get options () {
    return {
      loop: true,
      slides: {
        origin: 'center',
        perView: 1.111,
        spacing: 10,
      },
      breakpoints: {
        '(min-width: 768px)': {
          slides: { origin: 'center', perView: 1.5, spacing: 20 },
        },
      },
    }
  }

  /** @returns {KeenSliderPlugin[]} */
  get plugins () {
    return [
      setInitialised(),
      useArrows({
        next: this.els.next.element,
        previous: this.els.previous.element,
      }),
    ]
  }

  /**
   * Replaces the slider's slides with new markup (e.g. when a
   * product-compare swatch is selected) and re-evaluates whether a
   * carousel is needed for the new slide count - destroying one that's
   * no longer needed, or lazily creating one that now is.
   *
   * @param {string} html
   */
  async swapSlides (html) {
    this.els.slider.element.innerHTML = html

    if (!this.hasMultipleSlides) {
      this.carousel?.destroy()
      this.carousel = null
      this.els.slider.element.classList.remove('keen-slider')
      return
    }

    if (!this.carousel) {
      this.carousel = await this.initialiseCarousel()
      return
    }

    this.attachCarouselClasses()
    this.carousel.update(this.options, 0)
  }
}

if (!customElements.get('product-compare-media')) {
  customElements.define('product-compare-media', ProductCompareMedia)
}
